import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { formatMm } from '@bw/core';
import {
  DEFAULT_SETUP,
  RESOLUTION_PX_PER_MM,
  SCALE_RATIOS,
  SHEET_SIZES,
  rasterLayout,
} from '@bw/export';
import type {
  ColourMode,
  DimensionMode,
  JointMode,
  Orientation,
  RasterResolution,
  ScaleRatio,
  SheetSetup,
  SheetSizeId,
} from '@bw/export';
import { Modal } from './Dialogs.js';
import { useEditor } from '../state/store.js';
import { downloadBytes, exportPdf, exportPng, previewSheetCount } from '../editor/exporting.js';

export interface ExportDialogProps {
  readonly onClose: () => void;
  readonly onStatus: (text: string) => void;
}

const RESOLUTIONS: readonly { readonly id: RasterResolution; readonly label: string }[] = [
  { id: 'screen', label: 'Screen' },
  { id: 'print', label: 'Print' },
  { id: 'maximum', label: 'Maximum' },
];

export function ExportDialog({ onClose, onStatus }: ExportDialogProps): JSX.Element {
  const project = useEditor((state) => state.project);
  const wall = project.activeWall;
  const [tab, setTab] = useState<'pdf' | 'png'>('pdf');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolution, setResolution] = useState<RasterResolution>('print');
  const [setup, setSetup] = useState<SheetSetup>(() => ({
    ...DEFAULT_SETUP,
    title: {
      project: project.name,
      wall: wall.name,
      drawnBy: '',
      revision: 'A',
      date: new Date().toISOString().slice(0, 10),
    },
  }));

  useEffect(() => {
    setSetup((current) => ({
      ...current,
      title: { ...current.title, project: project.name, wall: wall.name },
    }));
  }, [project.name, wall.name]);

  const sheets = useMemo(() => previewSheetCount(project, setup), [project, setup]);
  const bounds = wall.model.bounds();
  const raster = useMemo(() => {
    if (bounds === null) return null;
    try {
      return rasterLayout(bounds, resolution);
    } catch {
      return null;
    }
  }, [bounds, resolution]);

  const patch = (changes: Partial<SheetSetup>): void => setSetup((current) => ({ ...current, ...changes }));
  const patchTitle = (changes: Partial<SheetSetup['title']>): void =>
    setSetup((current) => ({ ...current, title: { ...current.title, ...changes } }));

  const empty = wall.model.size === 0;

  const doExport = (): void => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const base = project.name.trim() === '' ? 'Untitled' : project.name.trim();
        if (tab === 'pdf') {
          const outcome = await exportPdf(project, setup);
          downloadBytes(`${base}.pdf`, outcome.bytes, 'application/pdf');
          onStatus(`Exported ${base}.pdf · ${outcome.detail}.`);
        } else {
          const outcome = await exportPng(project, resolution);
          downloadBytes(`${base}.png`, outcome.bytes, 'image/png');
          onStatus(`Exported ${base}.png · ${outcome.detail}.`);
        }
        onClose();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Modal
      title="Export"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn cta" onClick={doExport} disabled={busy || empty}>
            {busy ? 'Working…' : tab === 'pdf' ? 'Export drawing' : 'Export image'}
          </button>
        </>
      }
    >
      <div className="tabs">
        <button className="btn" aria-pressed={tab === 'pdf'} onClick={() => setTab('pdf')}>
          Drawing sheets (PDF)
        </button>
        <button className="btn" aria-pressed={tab === 'png'} onClick={() => setTab('png')}>
          Image (PNG)
        </button>
      </div>

      {empty && <p className="note warn">Lay at least one brick before exporting.</p>}

      {tab === 'pdf' ? (
        <>
          <div className="grid2">
            <label>
              Sheet size
              <select
                value={setup.sheet}
                onChange={(event) => patch({ sheet: event.target.value as SheetSizeId })}
              >
                {SHEET_SIZES.map((size) => (
                  <option key={size.id} value={size.id}>
                    {size.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Orientation
              <select
                value={setup.orientation}
                onChange={(event) => patch({ orientation: event.target.value as Orientation })}
              >
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </select>
            </label>
            <label>
              Scale
              <select
                value={setup.ratio}
                onChange={(event) => patch({ ratio: Number(event.target.value) as ScaleRatio })}
              >
                {SCALE_RATIOS.map((ratio) => (
                  <option key={ratio} value={ratio}>
                    1:{ratio}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Dimensions
              <select
                value={setup.dimensions}
                onChange={(event) => patch({ dimensions: event.target.value as DimensionMode })}
              >
                <option value="overall">Overall only</option>
                <option value="gauge">Overall and course gauge</option>
                <option value="full">Full setting-out</option>
              </select>
            </label>
            <label>
              Colour
              <select
                value={setup.colour}
                onChange={(event) => patch({ colour: event.target.value as ColourMode })}
              >
                <option value="colour">Full colour</option>
                <option value="mono">Mono line work</option>
              </select>
            </label>
            <label>
              Joints
              <select
                value={setup.joints}
                onChange={(event) => patch({ joints: event.target.value as JointMode })}
              >
                <option value="gaps">Drawn as gaps</option>
                <option value="filled">Filled with a mortar tone</option>
              </select>
            </label>
          </div>

          <label className="check" style={{ marginTop: 10 }}>
            <input
              type="checkbox"
              checked={setup.showGrid}
              onChange={(event) => patch({ showGrid: event.target.checked })}
            />
            Print a faint grid
          </label>

          <h3>Title block</h3>
          <div className="grid2">
            <label>
              Project
              <input data-text-input value={setup.title.project} onChange={(event) => patchTitle({ project: event.target.value })} />
            </label>
            <label>
              Wall
              <input data-text-input value={setup.title.wall} onChange={(event) => patchTitle({ wall: event.target.value })} />
            </label>
            <label>
              Drawn by
              <input data-text-input value={setup.title.drawnBy} onChange={(event) => patchTitle({ drawnBy: event.target.value })} />
            </label>
            <label>
              Revision
              <input data-text-input value={setup.title.revision} onChange={(event) => patchTitle({ revision: event.target.value })} />
            </label>
          </div>

          <p className={sheets !== null && sheets > 1 ? 'note ok' : 'note'}>
            {sheets === null
              ? 'Nothing to draw yet.'
              : `${sheets} sheet${sheets === 1 ? '' : 's'}, ${setup.sheet} ${setup.orientation}, 1:${setup.ratio}.` +
                (sheets > 1
                  ? ' Tiles overlap by 10 mm with printed match lines, and every sheet carries the title block, scale bar and a key plan.'
                  : '')}
            {bounds !== null &&
              ` The wall is ${formatMm(bounds.maxX - bounds.minX)} × ${formatMm(bounds.maxY - bounds.minY)} mm, so it measures ${formatMm((bounds.maxX - bounds.minX) / setup.ratio)} mm on paper.`}
          </p>
        </>
      ) : (
        <>
          <div className="grid2">
            <label>
              Resolution
              <select
                value={resolution}
                onChange={(event) => setResolution(event.target.value as RasterResolution)}
              >
                {RESOLUTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} · {RESOLUTION_PX_PER_MM[option.id]} px/mm
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="note">
            {raster === null
              ? 'Nothing to render yet.'
              : `${raster.width} × ${raster.height} px at ${raster.scale.toFixed(3)} px/mm.` +
                ' The image fits every laid brick and excludes the grid, selection outlines and previews.'}
          </p>
        </>
      )}

      {error !== null && <p className="note warn">{error}</p>}
    </Modal>
  );
}
